import { useState } from 'react';
import type { ReactNode } from 'react';
import { Card, Button, Badge, Input } from '@lib/ui';
import {
  Modal,
  TabGroup,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Dropdown,
  DropdownItem,
} from '@lib/ui';

// Icons
const PlusIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
  </svg>
);

const DocumentIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"
    />
  </svg>
);

const LinkIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
    />
  </svg>
);

const NoteIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125"
    />
  </svg>
);

const RefreshIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
    />
  </svg>
);

const MoreIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-5"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
    />
  </svg>
);

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"
    />
  </svg>
);

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className="size-4"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

type SourceType = 'document' | 'url' | 'note';

interface Source {
  id: number;
  name: string;
  type: SourceType;
  insights: number;
  lastUpdated: string;
  status: 'active' | 'processing' | 'syncing';
  tags: string[];
}

const sources: Source[] = [
  {
    id: 1,
    name: 'Product Vision Document',
    type: 'document',
    insights: 8,
    lastUpdated: '2 hours ago',
    status: 'active',
    tags: ['strategy', 'vision'],
  },
  {
    id: 2,
    name: 'Q4 Market Analysis Report',
    type: 'document',
    insights: 12,
    lastUpdated: '1 day ago',
    status: 'active',
    tags: ['market', 'research'],
  },
  {
    id: 3,
    name: 'Industry Trends 2024',
    type: 'url',
    insights: 6,
    lastUpdated: '3 days ago',
    status: 'syncing',
    tags: ['trends'],
  },
  {
    id: 4,
    name: 'AI Research Paper: LLMs in Content',
    type: 'document',
    insights: 15,
    lastUpdated: '1 week ago',
    status: 'active',
    tags: ['AI', 'research'],
  },
  {
    id: 5,
    name: 'Competitor Analysis Notes',
    type: 'note',
    insights: 4,
    lastUpdated: '2 weeks ago',
    status: 'active',
    tags: ['competition'],
  },
  {
    id: 6,
    name: 'User Feedback Collection',
    type: 'document',
    insights: 9,
    lastUpdated: '3 days ago',
    status: 'active',
    tags: ['users', 'feedback'],
  },
  {
    id: 7,
    name: 'Blog Post Ideas',
    type: 'note',
    insights: 7,
    lastUpdated: '5 days ago',
    status: 'processing',
    tags: ['content', 'ideas'],
  },
  {
    id: 8,
    name: 'TechCrunch Article: Future of AI',
    type: 'url',
    insights: 3,
    lastUpdated: '1 day ago',
    status: 'active',
    tags: ['AI', 'news'],
  },
];

const typeIcons: Record<SourceType, ReactNode> = {
  document: <DocumentIcon />,
  url: <LinkIcon />,
  note: <NoteIcon />,
};

const typeColors: Record<SourceType, string> = {
  document: 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400',
  url: 'bg-info-100 text-info-600 dark:bg-info-900/30 dark:text-info-400',
  note: 'bg-warning-100 text-warning-600 dark:bg-warning-900/30 dark:text-warning-400',
};

const statusColors: Record<string, 'success' | 'warning' | 'info'> = {
  active: 'success',
  processing: 'warning',
  syncing: 'info',
};

export default function Sources() {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const filteredSources = sources.filter(
    (source) =>
      source.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      source.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Sources</h1>
          <p className="mt-1 text-gray-500 dark:text-dark-300">
            Sources are sacred and traceable. Manage your living inputs.
          </p>
        </div>
        <Button onClick={() => setAddModalOpen(true)}>
          <PlusIcon />
          <span className="ml-2">Add Source</span>
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Input
          placeholder="Search sources or tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="sm:w-80"
          prefix={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-4"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
              />
            </svg>
          }
        />
        <div className="flex items-center gap-2">
          <Badge variant="soft" color="primary">
            {filteredSources.length} sources
          </Badge>
          <Badge variant="soft" color="secondary">
            {filteredSources.reduce((acc, s) => acc + s.insights, 0)} insights
          </Badge>
        </div>
      </div>

      {/* Sources Grid */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {filteredSources.map((source) => (
          <Card key={source.id} className="p-4 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div
                  className={`flex-shrink-0 size-10 rounded-lg flex items-center justify-center ${typeColors[source.type]}`}
                >
                  {typeIcons[source.type]}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {source.name}
                  </h3>
                  <div className="mt-1 flex items-center gap-2">
                    <Badge
                      variant="soft"
                      color={statusColors[source.status]}
                      className="text-xs capitalize"
                    >
                      {source.status}
                    </Badge>
                    <span className="text-xs text-gray-500 dark:text-dark-300">
                      {source.lastUpdated}
                    </span>
                  </div>
                </div>
              </div>
              <Dropdown
                trigger={
                  <Button variant="flat" isIcon className="size-8">
                    <MoreIcon />
                  </Button>
                }
              >
                <DropdownItem>
                  <EyeIcon />
                  <span className="ml-2">View</span>
                </DropdownItem>
                <DropdownItem>
                  <RefreshIcon />
                  <span className="ml-2">Refresh</span>
                </DropdownItem>
                <DropdownItem className="text-error-600 dark:text-error-400">
                  <TrashIcon />
                  <span className="ml-2">Delete</span>
                </DropdownItem>
              </Dropdown>
            </div>

            {/* Tags */}
            <div className="mt-3 flex flex-wrap gap-1">
              {source.tags.map((tag) => (
                <Badge key={tag} variant="outlined" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Insights Count */}
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-dark-600 flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-dark-200">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="size-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 18v-5.25m0 0a6.01 6.01 0 0 0 1.5-.189m-1.5.189a6.01 6.01 0 0 1-1.5-.189m3.75 7.478a12.06 12.06 0 0 1-4.5 0m3.75 2.383a14.406 14.406 0 0 1-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 1 0-7.517 0c.85.493 1.509 1.333 1.509 2.316V18"
                  />
                </svg>
                <span>{source.insights} insights</span>
              </div>
              <Button variant="soft" className="text-xs px-2 py-1">
                Extract More
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {/* Add Source Modal */}
      <Modal isOpen={addModalOpen} onClose={() => setAddModalOpen(false)} title="Add New Source">
        <div className="p-4">
          <TabGroup defaultValue="upload">
            <TabList>
              <Tab value="upload">Upload Document</Tab>
              <Tab value="url">Paste URL</Tab>
              <Tab value="note">Write Note</Tab>
            </TabList>
            <TabPanels className="mt-4">
              <TabPanel value="upload">
                <div className="border-2 border-dashed border-gray-200 dark:border-dark-500 rounded-lg p-8 text-center">
                  <DocumentIcon />
                  <p className="mt-2 text-sm text-gray-600 dark:text-dark-300">
                    Drag and drop your file here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-gray-400 dark:text-dark-400">
                    Supports PDF, DOC, DOCX, TXT, MD
                  </p>
                  <Button variant="soft" className="mt-4">
                    Browse Files
                  </Button>
                </div>
              </TabPanel>
              <TabPanel value="url">
                <div className="space-y-4">
                  <Input
                    label="URL"
                    placeholder="https://example.com/article"
                    prefix={<LinkIcon />}
                  />
                  <p className="text-xs text-gray-500 dark:text-dark-300">
                    We'll fetch and analyze the content automatically. Works with articles, blog
                    posts, and documentation.
                  </p>
                </div>
              </TabPanel>
              <TabPanel value="note">
                <div className="space-y-4">
                  <Input label="Title" placeholder="Give your note a title" />
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-1">
                      Content
                    </label>
                    <textarea
                      className="w-full h-32 px-3 py-2 border border-gray-200 dark:border-dark-500 rounded-lg bg-white dark:bg-dark-700 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="Write your thoughts, ideas, or raw content here..."
                    />
                  </div>
                </div>
              </TabPanel>
            </TabPanels>
          </TabGroup>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100 dark:border-dark-600">
            <Button variant="flat" onClick={() => setAddModalOpen(false)}>
              Cancel
            </Button>
            <Button>Add Source</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
